import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import GenericModal from '../generic_modal';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';
import {
	countReached,
	forwardBoundary,
	hasForwardOnlyChange,
	InstanceState,
} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/taskSetReach';

export interface TaskSetScopeModalPayload {
	/** The field names the reader changed, in the server's vocabulary. */
	changedFields: ReadonlyArray<string>;
	instances: ReadonlyArray<InstanceState>;
	/** Null applies to every eligible instance; a number is the forward boundary. */
	onApply: (fromSequence: number | null) => void;
}

export const showTaskSetScopeModal = (props: TaskSetScopeModalPayload): void => {
	showModal({
		type: MODAL_TYPE.TASK_SET_SCOPE,
		...props,
	});
};

/** Each field in the reader's words, so the question names what is being scoped. */
const FIELD_KEYS: {[field: string]: string} = {
	setName: 'task_set.field.set_name',
	name: 'task_set.field.instance_name',
	description: 'task_set.field.description',
	estimate: 'task_set.field.estimate',
	role: 'task_set.field.role',
	phase: 'task_set.field.phase',
	assigned: 'task_set.field.assigned',
	labels: 'task_set.field.labels',
	billable: 'task_set.field.billable',
};

const Lead = styled.p`
	margin: 0 0 14px;
	font-size: 13px;
	line-height: 19px;
	color: rgba(0, 0, 0, 0.87);
`;

const Option = styled.label<{selected: boolean}>`
	display: grid;
	grid-template-columns: 18px 1fr;
	gap: 10px;
	align-items: start;
	padding: 12px;
	margin-bottom: 8px;
	border: 1px solid ${({selected}) => (selected ? '#0e3299' : '#e6e6e6')};
	border-radius: 4px;
	cursor: pointer;
`;

const OptionTitle = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
`;

const OptionDetail = styled.span`
	display: block;
	margin-top: 3px;
	font-size: 12px;
	line-height: 17px;
	color: #7d7d7d;
`;

/** The count sits with its own option: each one moves a different amount of work. */
const OptionCount = styled.span`
	display: block;
	margin-top: 5px;
	font-size: 12px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	color: rgba(0, 0, 0, 0.87);
`;

const Note = styled.p`
	margin: 12px 0 0;
	font-size: 12px;
	line-height: 18px;
	color: #7d7d7d;
`;

/**
 * How far along the series an edit should reach.
 *
 * <p>Shown on Save rather than before it: which fields changed decides what this
 * says, so it cannot be drawn until the reader has finished editing.
 *
 * <p>Two options, never three. "This instance only" has nothing to point at from
 * the set drawer -- editing one instance is what the task modal already does, and
 * routing it through the set would be a second way to the same outcome that also
 * marks the instance as diverged.
 *
 * <p>It is not shown at all for a set nothing has touched. Every instance is
 * identical then, so the question would have one answer.
 */
export const TaskSetScopeModal = ({
	closeModal,
	changedFields,
	instances,
	onApply,
}: ModalProps<TaskSetScopeModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	// Offered only when the scope can actually narrow something. Nothing the
	// reader changed is forward-only means the boundary reaches the same
	// instances as "all eligible", and the option's own wording -- "leaving the
	// two before it as they are" -- would then be false.
	const boundary = hasForwardOnlyChange(changedFields) ? forwardBoundary(instances) : null;
	const [fromSequence, setFromSequence] = useState<number | null>(null);

	const allCount = countReached(instances, changedFields, null);
	const forwardCount = boundary === null ? allCount : countReached(instances, changedFields, boundary);
	const selectedCount = fromSequence === null ? allCount : forwardCount;
	const before = boundary === null ? 0 : instances.filter(i => (i.sequence ?? 0) < boundary).length;

	const fieldNames = changedFields
		.map(field => (FIELD_KEYS[field] ? formatMessage({id: FIELD_KEYS[field]}) : field))
		.join(', ');

	const handleApply = () => {
		onApply(fromSequence);
		closeModal();
	};

	const content = (
		<div data-cy="task-set-scope-modal-content">
			<Lead>{formatMessage({id: 'task_set.scope.changed_fields'}, {fields: fieldNames})}</Lead>

			<Option selected={fromSequence === null}>
				<input
					type="radio"
					name="task-set-scope"
					checked={fromSequence === null}
					onChange={() => setFromSequence(null)}
					data-cy="task-set-scope-all"
				/>
				<span>
					<OptionTitle>{formatMessage({id: 'task_set.scope.all_eligible'})}</OptionTitle>
					<OptionDetail>{formatMessage({id: 'task_set.scope.all_eligible_detail'})}</OptionDetail>
					<OptionCount>
						{formatMessage({id: 'task_set.scope.option_count'}, {count: allCount, total: instances.length})}
					</OptionCount>
				</span>
			</Option>

			{/* Only when there is a boundary to offer AND something forward-only to
			    hold back. Otherwise the two options reach the same instances, and a
			    second radio that changes nothing is one question wearing two hats --
			    worse here, because its wording would promise earlier instances are
			    left alone when a rename reaches them regardless. */}
			{boundary !== null ? (
				<Option selected={fromSequence !== null}>
					<input
						type="radio"
						name="task-set-scope"
						checked={fromSequence !== null}
						onChange={() => setFromSequence(boundary)}
						data-cy="task-set-scope-forward"
					/>
					<span>
						<OptionTitle>{formatMessage({id: 'task_set.scope.forward'}, {sequence: boundary})}</OptionTitle>
						<OptionDetail>
							{formatMessage({id: 'task_set.scope.forward_detail'}, {sequence: boundary, before})}
						</OptionDetail>
						<OptionCount>
							{formatMessage({id: 'task_set.scope.option_count'}, {count: forwardCount, total: allCount})}
						</OptionCount>
					</span>
				</Option>
			) : null}

			{/* Honest rather than blocking: the set's own defaults still change, and
			    later instances are created from them. */}
			{selectedCount === 0 ? <Note>{formatMessage({id: 'task_set.scope.reaches_nothing'})}</Note> : null}
		</div>
	);

	return (
		<GenericModal
			closeModal={closeModal}
			cy="task-set-scope-modal"
			headerText={formatMessage({id: 'task_set.scope.title'})}
			content={content}
			buttons={[
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
					cy: 'task-set-scope-cancel',
				},
				{
					// The count rides on the button, and it is the count for the
					// option actually selected -- not the larger of the two.
					text:
						selectedCount === 0
							? formatMessage({id: 'task_set.scope.apply_none'})
							: formatMessage({id: 'task_set.scope.apply'}, {count: selectedCount}),
					callback: handleApply,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					cy: 'task-set-scope-apply',
				},
			]}
		/>
	);
};

export default TaskSetScopeModal;
